import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { pageLabel } from "@/i18n/pageLabel";
import { fileUrl } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import UserMenu from "@/components/UserMenu";
import {
  Bookmark, Anchor, Flag, Palette, Type, Image as ImageIcon, LayoutGrid, Package,
  FileText, Plus, ChevronLeft, ChevronRight, Settings, LifeBuoy,
  Sparkles, PenLine, Image as ImageTool, Wrench, MessageCircle,
} from "lucide-react";

const pageIcon = (name) => {
  const map = {
    "Introductie": Bookmark, "Introduction": Bookmark,
    "Het merk": Anchor, "Brand Strategy": Anchor, "Merkstrategie": Anchor, "The brand": Anchor,
    "Tone of voice": MessageCircle, "Tone": MessageCircle,
    "Het logo": Flag, "Logo": Flag,
    "Kleuren": Palette, "Colors": Palette, "Color": Palette,
    "Typografie": Type, "Typography": Type,
    "Art direction": ImageIcon, "Art Direction": ImageIcon,
    "Merksysteem": LayoutGrid, "Brand System": LayoutGrid,
    "Concept middelen": Package, "Applications": Package,
  };
  return map[name] || FileText;
};

const toolDefs = (t) => [
  { key: "agent", label: t("sidebar.tool_agent"), badge: t("sidebar.badge_soon"), icon: Sparkles, disabled: false },
  { key: "writer", label: t("sidebar.tool_writer"), badge: t("sidebar.badge_pro"), icon: PenLine, disabled: false },
  { key: "creator", label: t("sidebar.tool_creator"), badge: t("sidebar.badge_pro"), icon: ImageTool, disabled: false },
  { key: "tool", label: t("sidebar.tool_tool"), badge: t("sidebar.badge_pro"), icon: Wrench, disabled: true },
];

export default function Sidebar({
  brands, activeBrandId,
  pages, activePageId, activeTool, onSelectPage, onSelectTool,
  canEdit, onAddPage, user, role,
}) {
  const { logout } = useAuth();
  const { t } = useI18n();
  const activeBrand = brands.find((b) => b.id === activeBrandId);
  const [collapsed, setCollapsed] = useState(() => (typeof window !== "undefined" && localStorage.getItem("bh_sidebar_collapsed") === "1"));

  useEffect(() => {
    localStorage.setItem("bh_sidebar_collapsed", collapsed ? "1" : "0");
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--bh-sidebar-w", collapsed ? "4rem" : "16rem");
    }
  }, [collapsed]);

  const TOOLS = toolDefs(t);
  const avatarUrl = user?.avatar_file_id ? fileUrl(user.avatar_file_id) : user?.picture;

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        data-testid="sidebar"
        className={`${collapsed ? "w-16" : "w-64"} h-screen fixed left-0 top-0 z-[60] bg-[#f3f3f1] hidden md:flex flex-col text-[#1a1a1a] transition-[width] duration-300 ease-out`}
      >
        {/* Collapse tab */}
        <button
          data-testid="toggle-collapse"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          className="absolute top-5 -right-3 z-[61] w-6 h-6 rounded-md border border-gray-200 bg-white shadow-sm hover:bg-gray-50 flex items-center justify-center transition-colors"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {/* Brand header */}
        <div className={`${collapsed ? "px-2 pt-4 pb-8" : "pl-3 pr-6 pt-4 pb-8"} transition-all duration-300`}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/brands"
                  data-testid="brand-switcher"
                  className="w-10 h-10 mx-auto rounded-md flex items-center justify-center hover:bg-white/60 transition-colors"
                  title={activeBrand?.name || t("sidebar.company_name")}
                >
                  <div
                    className="w-6 h-6 rounded-md shadow-sm"
                    style={{ background: activeBrand?.color_accent || "#fe7f57" }}
                  />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">
                {activeBrand?.name || t("sidebar.company_name")} · <span className="opacity-70">{t("sidebar.switch_brand_hint")}</span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Link
              to="/brands"
              data-testid="brand-switcher"
              className="block px-2 py-1.5 rounded-md hover:bg-white/70 transition-colors group"
              title={t("sidebar.switch_brand")}
            >
              <div className="text-[11px] text-gray-500 leading-tight">{t("sidebar.the_brand_hub")}</div>
              <div
                className="font-medium text-[15px] leading-tight truncate text-[#1a1a1a] group-hover:text-black"
                data-testid="sidebar-brand-name"
              >
                {activeBrand?.name || t("sidebar.company_name")}
              </div>
            </Link>
          )}
        </div>

        {/* Nav sections */}
        <div className={`flex-1 overflow-y-auto bh-scroll ${collapsed ? "px-2" : "px-3"} pb-4 space-y-6 transition-all duration-300`}>
          {/* The Brand */}
          <div>
            {!collapsed && <div className="px-3 mb-1.5 text-[11px] text-gray-500">{t("sidebar.section_the_brand")}</div>}
            <nav className="space-y-1">
              {pages.map((p) => {
                const Icon = pageIcon(p.name);
                const active = p.id === activePageId && !activeTool;
                const btn = (
                  <button
                    key={p.id}
                    data-testid={`nav-page-${p.id}`}
                    onClick={() => onSelectPage(p.id)}
                    className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-2 px-2.5"} py-1 rounded-md text-[14px] transition-colors ${
                      active ? "bg-white text-[#1a1a1a] font-medium shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                    {!collapsed && <span className="flex-1 text-left truncate">{pageLabel(p, t)}</span>}
                  </button>
                );
                return collapsed ? (
                  <Tooltip key={p.id}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right">{pageLabel(p, t)}</TooltipContent>
                  </Tooltip>
                ) : btn;
              })}
              {canEdit && !collapsed && (
                <button
                  data-testid="add-page-btn"
                  onClick={onAddPage}
                  className="w-full text-left pl-3 py-1.5 text-[13px] text-gray-400 hover:text-gray-700"
                >
                  {t("sidebar.add_page")}
                </button>
              )}
              {canEdit && collapsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button data-testid="add-page-btn" onClick={onAddPage} className="w-full flex items-center justify-center py-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-white/60">
                      <Plus className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t("sidebar.add_page_tooltip")}</TooltipContent>
                </Tooltip>
              )}
            </nav>
          </div>

          {/* The Tools */}
          <div>
            {!collapsed && <div className="px-3 mb-1.5 text-[11px] text-gray-500">{t("sidebar.section_the_tools")}</div>}
            <nav className="space-y-1">
              {TOOLS.map((tItem) => {
                const active = activeTool === tItem.key;
                const btn = (
                  <button
                    key={tItem.key}
                    data-testid={`tool-${tItem.key}`}
                    disabled={tItem.disabled}
                    onClick={() => !tItem.disabled && onSelectTool(tItem.key)}
                    className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "justify-between gap-2 px-2.5"} py-1 rounded-md text-[14px] transition-colors ${
                      active ? "bg-white text-[#1a1a1a] font-medium shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"
                    } ${tItem.disabled ? "opacity-80 cursor-default" : ""}`}
                  >
                    {collapsed ? (
                      <tItem.icon className="w-4 h-4" strokeWidth={1.75} />
                    ) : (
                      <>
                        <span className="truncate">{tItem.label}</span>
                        {tItem.badge && (
                          <span className="text-[9px] font-normal uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#dcc9f3] text-[#5b3a9e]">
                            {tItem.badge}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
                return collapsed ? (
                  <Tooltip key={tItem.key}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right">{tItem.label} {tItem.badge && <span className="ml-1 opacity-70">· {tItem.badge}</span>}</TooltipContent>
                  </Tooltip>
                ) : btn;
              })}
            </nav>
          </div>
        </div>

      {/* Settings / Support — match nav button styling */}
      <div className={`${collapsed ? "px-2" : "px-3"} pt-6 pb-3 space-y-1 transition-all duration-300`}>
        {collapsed ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <button data-testid="settings-btn" onClick={() => onSelectTool("settings", "appearance")} className={`w-full flex items-center justify-center py-1 rounded-md transition-colors ${activeTool === "settings" ? "bg-white text-[#1a1a1a] shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"}`}>
                  <Settings className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("sidebar.settings")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button data-testid="support-btn" onClick={() => onSelectTool("support")} className={`w-full flex items-center justify-center py-1 rounded-md transition-colors ${activeTool === "support" ? "bg-white text-[#1a1a1a] shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"}`}>
                  <LifeBuoy className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("sidebar.support")}</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            <button
              data-testid="settings-btn"
              onClick={() => onSelectTool("settings", "appearance")}
              className={`w-full flex items-center gap-2 px-2.5 py-1 rounded-md text-[14px] transition-colors ${
                activeTool === "settings" ? "bg-white text-[#1a1a1a] font-medium shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"
              }`}
            >
              <Settings className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 text-left truncate">{t("sidebar.settings")}</span>
            </button>
            <button
              data-testid="support-btn"
              onClick={() => onSelectTool("support")}
              className={`w-full flex items-center gap-2 px-2.5 py-1 rounded-md text-[14px] transition-colors ${
                activeTool === "support" ? "bg-white text-[#1a1a1a] font-medium shadow-sm" : "text-[#1a1a1a] hover:bg-white/60"
              }`}
            >
              <LifeBuoy className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 text-left truncate">{t("sidebar.support")}</span>
            </button>
          </>
        )}
      </div>

        {/* Profile card — opens the global user menu (Profile Settings / Log out). */}
        <div className={`${collapsed ? "px-2" : "px-3"} pb-3 transition-all duration-300`}>
          <UserMenu align="start" side="top">
            <button
              data-testid="profile-trigger"
              className={`w-full bg-white rounded-xl ${collapsed ? "p-2 justify-center" : "p-3 gap-3"} flex items-center shadow-sm hover:bg-gray-50 transition-colors text-left`}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" data-testid="profile-avatar-img" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gray-300 shrink-0" data-testid="profile-avatar-fallback" />
              )}
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-gray-500 leading-tight">{t("sidebar.profile_label")}</div>
                  <div className="font-medium text-[14px] leading-tight truncate">{user?.name || "Jane Doe"}</div>
                </div>
              )}
            </button>
          </UserMenu>
        </div>
      </aside>
    </TooltipProvider>
  );
}
